/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  ActionReducerMapBuilder,
  createAction,
  createAsyncThunk
} from '@reduxjs/toolkit';

import { getSkill } from '@ibm-watson/shared/modules/ia/store/skills/actions';
import { ApiStatus } from '../../../../../ts';
import { Skill, SkillStatus } from '../../../../../ts/skill';
import {
  AsyncAppThunk,
  CreateAsyncThunk,
  SkillsSlice
} from '../../../../../ts/state';
import * as API from '../../../api/skills';
import { getErrorString } from '../../../util/errorHandler';
import {
  createdObjectEvents,
  updatedObjectEvents
} from '../../../util/trackers/segment';
import { selectIsAccessUpdatesEnabled } from '../../features/selectors';
import { selectIsNewIa } from '../../instance/selectors';
import {
  addErrorMessage,
  addMessage,
  AddToastMessage,
  ToastStatus
} from '../../messages/actions';
import { getAssistantId } from '../../session/selectors';
import { entityAdapter } from '../adapter';
import { isProcessing, sleep } from '../helper';
import { deleteSkill } from './delete';

const DO_SKILL_IMPORT = 'Skill uploaded successfully.';
const NO_SKILL_IMPORT = 'Skill upload failed. ';

type ImportSkill = Partial<Skill> & {
  async: boolean;
  importedFile: string;
  options: Record<string, string>;
};

/**
 * Imports a new skill. The server desides based on the skill size
 * if the import will be synchronous or not
 * @param {string} params.skill_id The skill id if the import is to an existing skill
 * @param {string} params.options Import options
 * @param {*} params.importedFile The file to import
 * @param {boolean} params.async If true the import will be asynchronous
 * @returns {AsyncThunk} The async action
 */
export const importSkill = createAsyncThunk<
  Skill,
  ImportSkill,
  CreateAsyncThunk
>('skills/import', async (params, { dispatch, getState }) => {
  const { skill_id, options, importedFile, async } = params;
  const event = skill_id ? updatedObjectEvents : createdObjectEvents;
  const isNewSkill = skill_id ? false : true;

  // Use revised API for Access Control improvements if enabled & isNewIA
  // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler new_ia on call to importSkill
  const assistantId = getAssistantId(getState());
  const new_ia = selectIsNewIa(getState());
  const useNewIAUpdatedAccess =
    new_ia && selectIsAccessUpdatesEnabled(getState());

  const data = new FormData();
  data.append('data', JSON.stringify({ options }));
  data.append('file', importedFile);
  const config = {
    params: { async, new_ia },
    onProgress: (progress: number) => dispatch(uploadProgress(progress))
  };
  try {
    let skill = await API.importSkill(
      skill_id,
      data,
      config,
      useNewIAUpdatedAccess,
      assistantId
    );
    if (!skill.workspace_id) {
      skill.workspace_id = skill.skill_id;
    }

    // Importing a skill is async for the UI so we need to monitor it
    await dispatch(checkImportProgress(isNewSkill, skill));

    // Update the skill once the upload status changes.
    skill = await dispatch(getSkill(skill.skill_id)).unwrap();

    return skill;
  } catch (error) {
    if (!options?.importRetry) {
      dispatch(addErrorMessage(error));
      event.skill({
        processType: 'import',
        resultValue: getErrorString(error),
        successFlag: false
      });
      throw error;
    } else if (
      error.response?.status === 400 &&
      error.response?.data?.error === 'Skill is unavailable'
    ) {
      await dispatch(deleteSkill(skill_id));
      return dispatch(
        importSkill({
          ...params,
          skill_id: null,
          options: { ...options, importRetry: false }
        })
      ).unwrap();
    }
    throw error;
  }
});

/**
 * Checks the API while the skill is processing and keeps redux updated.
 * @param {*} isNewSkill True if the skill is new
 * @param {*} importedSkill The imported skill
 * @returns {*} The skill once is done processing
 */
export const checkImportProgress =
  (isNewSkill: boolean, importedSkill: Skill): AsyncAppThunk =>
  async (dispatch, getState) => {
    let skill = importedSkill;
    let waitTimeInMs = 15000; // 15 seconds

    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on call to listVariable
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());

    // Wait until the skill is processed and dispatch updates in the meantime
    while (isProcessing(skill)) {
      try {
        dispatch(notifyImportStatus(skill));
        await sleep(waitTimeInMs);
        skill = await API.getSkill(
          skill.skill_id,
          useNewIAUpdatedAccess,
          assistantId
        );
      } catch (error) {
        // Wait a bit more if there is an error
        waitTimeInMs = 120000;
      }
    }

    let messageErrors = '';
    if (skill.status_errors) {
      skill.status_errors.forEach(e => (messageErrors += e.message));
    }

    let message: AddToastMessage = {
      message: DO_SKILL_IMPORT,
      status: ToastStatus.SUCCESS
    };
    if (skill.status === SkillStatus.FAILED) {
      message = {
        message: NO_SKILL_IMPORT + messageErrors,
        status: ToastStatus.ERROR
      };
    }

    // Notify the user if the import successed or not
    dispatch(addMessage(message));

    // Notify analytics
    const event = isNewSkill ? createdObjectEvents : updatedObjectEvents;
    event.skill({
      object: skill.skill_id,
      processType: 'import',
      resultValue: 'success',
      successFlag: skill.status !== SkillStatus.FAILED
    });

    // Notify the store
    dispatch(notifyImportResult({ isNewSkill, skill }));
    return skill;
  };

export const uploadProgress = createAction<number>(
  'skills/import/upload-progress'
);
export const notifyImportStatus = createAction<Skill>('skills/import/status');
export const notifyImportResult = createAction<{
  isNewSkill: boolean;
  skill: Skill;
}>('skills/import/result');
export const clearImportData = createAction('skills/import/clear');

const reducer = (builder: ActionReducerMapBuilder<SkillsSlice>) => {
  builder
    .addCase(importSkill.pending, (state, _action) => {
      state.importStatus = ApiStatus.LOADING;
      state.importError = null;
      state.importProgress = 0;
    })
    .addCase(importSkill.fulfilled, (state, action) => {
      state.importProgress = 0;
      state.importStatus = ApiStatus.IDLE;
      entityAdapter.upsertOne(state, action.payload);
    })
    .addCase(importSkill.rejected, (state, action) => {
      state.importStatus = ApiStatus.ERROR;
      state.importError = action.error;
    })
    .addCase(uploadProgress, (state, action) => {
      state.importProgress = action.payload;
    })
    .addCase(notifyImportStatus, (state, action) => {
      entityAdapter.upsertOne(state, action.payload);
    })
    .addCase(notifyImportResult, (state, action) => {
      state.importStatus = ApiStatus.SUCCEEDED;
      entityAdapter.upsertOne(state, action.payload.skill);
      state.importError = null;
    })
    .addCase(clearImportData, (state, _action) => {
      state.importStatus = ApiStatus.IDLE;
      state.importError = null;
      state.importProgress = 0;
    });
};

export default reducer;
